import React from "react";
import {
  LoaderCircle,
  Play,
  Square,
} from "lucide-react";
import {
  fetchDispatchAlertCaptureConfig,
  updateDispatchAlertCaptureConfig,
} from "../lib/systemConfigApi.js";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  pillToneClasses,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";

const PRIMARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.primary,
]);
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);
const DANGER_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  "border-rose-200 bg-white text-rose-700 hover:border-rose-300 hover:bg-rose-50",
]);
const ALERT_CAPTURE_CONFIG_REFRESH_INTERVAL_MS = 30_000;

export default function DispatchAlertCaptureSettingsCard() {
  const [config, setConfig] = React.useState(null);
  const [isConfigLoading, setIsConfigLoading] = React.useState(true);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [configError, setConfigError] = React.useState("");
  const [feedback, setFeedback] = React.useState(null);
  const configRequestSequenceRef = React.useRef(0);

  const loadConfig = React.useCallback(async ({ quiet = false } = {}) => {
    const requestSequence = configRequestSequenceRef.current + 1;
    configRequestSequenceRef.current = requestSequence;
    if (!quiet) {
      setIsConfigLoading(true);
      setConfigError("");
    }
    try {
      const nextConfig = await fetchDispatchAlertCaptureConfig();
      if (requestSequence === configRequestSequenceRef.current) {
        setConfig(nextConfig);
        setConfigError("");
        return nextConfig;
      }
    } catch (error) {
      if (requestSequence === configRequestSequenceRef.current && !quiet) {
        setConfigError(error instanceof Error ? error.message : "Impossible de charger la configuration des captures.");
      }
    } finally {
      if (requestSequence === configRequestSequenceRef.current && !quiet) {
        setIsConfigLoading(false);
      }
    }
    return null;
  }, []);

  React.useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  React.useEffect(() => {
    if (
      !config?.is_active
      || typeof window === "undefined"
      || typeof document === "undefined"
    ) {
      return undefined;
    }

    let intervalId = null;
    let isDisposed = false;
    const clearPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const refreshQuietly = () => {
      if (!isDisposed && document.visibilityState !== "hidden" && !isUpdating) {
        void loadConfig({ quiet: true });
      }
    };
    const startPollingInterval = () => {
      clearPollingInterval();
      if (document.visibilityState !== "hidden") {
        intervalId = window.setInterval(refreshQuietly, ALERT_CAPTURE_CONFIG_REFRESH_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearPollingInterval();
        return;
      }
      refreshQuietly();
      startPollingInterval();
    };

    startPollingInterval();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      isDisposed = true;
      clearPollingInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [config?.is_active, isUpdating, loadConfig]);

  const handleStart = React.useCallback(async () => {
    if (!config || config.is_active || isUpdating) {
      return;
    }

    setIsUpdating(true);
    setFeedback(null);
    configRequestSequenceRef.current += 1;
    try {
      const nextConfig = await updateDispatchAlertCaptureConfig({
        expected_revision: config.revision,
        is_active: true,
        mode: "all",
        selected_user_ids: [],
      });
      setConfig(nextConfig);
      setFeedback({
        message: "La collecte des alertes Dispatch est maintenant active.",
        tone: "success",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Impossible d’activer la collecte.";
      const reloadedConfig = Number(error?.status) === 409
        ? await loadConfig({ quiet: true })
        : null;
      setFeedback({
        message: reloadedConfig
          ? `${errorMessage} La configuration courante a été rechargée; vous pouvez confirmer de nouveau.`
          : errorMessage,
        tone: "danger",
      });
    } finally {
      setIsUpdating(false);
    }
  }, [config, isUpdating, loadConfig]);

  const handleStop = React.useCallback(async () => {
    if (!config?.is_active || isUpdating) {
      return;
    }

    setIsUpdating(true);
    setFeedback(null);
    configRequestSequenceRef.current += 1;
    try {
      const nextConfig = await updateDispatchAlertCaptureConfig({
        expected_revision: config.revision,
        is_active: false,
        mode: config.mode,
        selected_user_ids: Array.isArray(config.selected_user_ids) ? config.selected_user_ids : [],
      });
      setConfig(nextConfig);
      setFeedback({
        message: "La collecte est arrêtée. Les captures déjà enregistrées restent disponibles.",
        tone: "info",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Impossible d’arrêter la collecte.";
      const reloadedConfig = Number(error?.status) === 409
        ? await loadConfig({ quiet: true })
        : null;
      setFeedback({
        message: reloadedConfig
          ? `${errorMessage} La configuration courante a été rechargée; vous pouvez confirmer l’arrêt de nouveau.`
          : errorMessage,
        tone: "danger",
      });
    } finally {
      setIsUpdating(false);
    }
  }, [config, isUpdating, loadConfig]);

  const selectedUserCount = Array.isArray(config?.selected_user_ids) ? config.selected_user_ids.length : 0;
  const campaignCaptureCount = Number.isFinite(Number(config?.campaign_capture_count))
    ? Number(config.campaign_capture_count)
    : 0;
  const maxCapturesPerCampaign = Number(config?.max_captures_per_campaign) || 500;
  const isCampaignNearLimit = campaignCaptureCount >= Math.ceil(maxCapturesPerCampaign * 0.9);
  const targetSummary = config?.mode === "selected"
    ? `${selectedUserCount} utilisateur${selectedUserCount > 1 ? "s" : ""} sélectionné${selectedUserCount > 1 ? "s" : ""}`
    : "Tous les utilisateurs du Dispatch";
  const canToggle = Boolean(config) && !isConfigLoading && !isUpdating;
  const feedbackClassName = feedback?.tone === "danger"
    ? statusToneClasses.danger
    : feedback?.tone === "success"
      ? statusToneClasses.success
      : statusToneClasses.info;

  return (
    <article
      className="grid gap-2.5 rounded-[20px] border border-black/6 bg-slate-50/75 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
      data-dispatch-alert-capture-settings="true"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid max-w-4xl gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-[0.94rem] font-semibold tracking-[-0.02em] text-slate-950">Captures des alertes Dispatch</p>
            <span className={joinClasses([
              "inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
              config?.is_active ? pillToneClasses.success : pillToneClasses.danger,
            ])}>
              {config?.is_active ? "Actif" : "Inactif"}
            </span>
          </div>
          <p className="m-0 text-[0.8rem] leading-5 text-slate-600">
            Enregistre des PNG recadrés sur les alertes rouges affichées dans le Dispatch, puis les conserve dans une page d’historique séparée.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {config?.is_active ? (
            <button className={DANGER_ACTION_CLASS_NAME} disabled={!canToggle} onClick={() => void handleStop()} type="button">
              {isUpdating ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <Square size={14} aria-hidden="true" />}
              Arrêter
            </button>
          ) : (
            <button
              aria-pressed="false"
              className={PRIMARY_ACTION_CLASS_NAME}
              disabled={!canToggle}
              onClick={() => void handleStart()}
              type="button"
            >
              {isUpdating ? <LoaderCircle className="animate-spin" size={15} aria-hidden="true" /> : <Play size={15} aria-hidden="true" />}
              Démarrer
            </button>
          )}
        </div>
      </div>

      {isConfigLoading ? (
        <div className="flex items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-3 py-2.5 text-[0.78rem] font-medium text-slate-600">
          <LoaderCircle className="animate-spin" size={15} aria-hidden="true" />
          Chargement de la configuration…
        </div>
      ) : configError ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[16px] border border-rose-200 bg-rose-50 px-3 py-2 text-[0.78rem] font-medium text-rose-700" role="alert">
          <span>{configError}</span>
          <button className={SECONDARY_ACTION_CLASS_NAME} onClick={() => void loadConfig()} type="button">Réessayer</button>
        </div>
      ) : config ? (
        <div className="flex flex-wrap gap-2 text-[0.76rem] text-slate-600">
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
            Cible: {targetSummary}
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
            Historique: {Number.isFinite(Number(config.capture_count)) ? Number(config.capture_count) : 0}
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
            Campagne: {campaignCaptureCount} / {maxCapturesPerCampaign}
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
            Conservation: {Number(config.retention_days) || 30} jours
          </span>
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700">
            Limite: {maxCapturesPerCampaign} par campagne
          </span>
        </div>
      ) : null}

      {config?.is_active && isCampaignNearLimit ? (
        <p className={joinClasses(["m-0 rounded-[16px] px-3 py-2 text-[0.78rem] font-medium", statusToneClasses.warning])} role="status">
          Cette campagne approche de sa limite ({campaignCaptureCount} / {maxCapturesPerCampaign} captures). Les nouvelles alertes cesseront d’être enregistrées une fois la limite atteinte.
        </p>
      ) : null}

      {feedback ? (
        <p className={joinClasses(["m-0 rounded-[16px] px-3 py-2 text-[0.78rem] font-medium", feedbackClassName])} role="status">
          {feedback.message}
        </p>
      ) : null}
    </article>
  );
}
